import { buildGraphqlQuery } from '@ztechapps/common.utils'
import { gql } from '@apollo/client'

// logisticGetShippingRates is a @Query, not a mutation — it persists nothing, mirroring
// OrderResolver.ts's orderGetShippingRates (libs/order/node/src/resolvers/OrderResolver.ts:280-287).
export const logisticGetShippingRatesQuery = gql`
  query logisticGetShippingRates($filter: GetLogisticShippingRatesArgs) {
    logisticGetShippingRates(filter: $filter) {
      _id
      shippingAmount
      deliveryDays
      estimatedDeliveryDate
      serviceType
      serviceCode
      carrierName
      carrierId
    }
  }
`

// The `Shipping` embedded subdocument fields the logistics screen reads — a narrower projection
// than the order module's own shippingFragment (libs/order/common/src/hooks/graphql.ts): no
// billTo/deliveryConfirmation/insuredAmount, since logistics has no bill-to accounts and no
// markup. `shippingCost` is the carrier charge the `Shipping Cost` row reads; the rest of the cost
// breakdown (amount, insuranceCost, confirmationCost, otherCost, markupCost) is still not read here.
export const logisticShippingFragment = gql`
  fragment logisticShippingFragment on Shipping {
    _id
    status
    shippingCost
    trackingNumber
    trackingUrl
    pdfLabel
    pngLabel
    zplLabel
    hrefLabel
    isVoidLabel
    voidMessage
    rate {
      _id
      shippingAmount
      deliveryDays
      estimatedDeliveryDate
      serviceType
      serviceCode
      carrierName
      carrierId
    }
    packages {
      _id
      weight {
        _id
        unit
        value
      }
      dimensions {
        _id
        unit
        length
        width
        height
      }
      insured_value {
        _id
        currency
        amount
      }
      label_download {
        _id
        png
        pdf
      }
      tracking_number
      package_code
      trackingUrl
      packageType
    }
  }
`

// The Logistic-level shipping fields, shared by the main document query and the five shipping
// mutations' return selections, so a mutation result refreshes the same fields the document read.
export const logisticShippingFieldsFragment = gql`
  fragment logisticShippingFieldsFragment on Logistic {
    _id
    shippingOrigin
    isManualShippingRate
    manualShippingCarrier
    totalWeight
    shipping {
      ...logisticShippingFragment
    }
  }
  ${logisticShippingFragment}
`

export const setLogisticCustomShippingCarrierQuery = gql`
  ${logisticShippingFieldsFragment}
  mutation setLogisticCustomShippingCarrier($input: SetLogisticCustomShippingCarrierArgs!) {
    setLogisticCustomShippingCarrier(input: $input) {
      ...logisticShippingFieldsFragment
    }
  }
`

export const setLogisticShippingRateQuery = gql`
  ${logisticShippingFieldsFragment}
  mutation setLogisticShippingRate($input: SetLogisticShippingRateArgs!) {
    setLogisticShippingRate(input: $input) {
      ...logisticShippingFieldsFragment
    }
  }
`

export const shipLogisticQuery = gql`
  ${logisticShippingFieldsFragment}
  mutation shipLogistic($input: ShipLogisticArgs!) {
    shipLogistic(input: $input) {
      ...logisticShippingFieldsFragment
    }
  }
`

export const voidLogisticShippingLabelQuery = gql`
  ${logisticShippingFieldsFragment}
  mutation voidLogisticShippingLabel($input: VoidLogisticShippingLabelArgs!) {
    voidLogisticShippingLabel(input: $input) {
      ...logisticShippingFieldsFragment
    }
  }
`

// The Address selection, written ONCE as a plain string and interpolated into both shapes that
// need it: the `addressFieldsFragment` below, which the item query spreads, and
// `logisticFragmentString`, which is pasted into queries carrying no fragment definition at all —
// a `...addressFieldsFragment` spread there would resolve to nothing.
//
// `_id` stays written literally in every selection set rather than living in this string, because
// @graphql-eslint parses the `gql` template with every `${…}` ERASED (@graphql-tools/
// graphql-tag-pluck `visitor.js:151-158`), so a selection set made only of a hole plucks to `{ }`
// and fails to parse. Precedent: the interpolations at `:352,421,429` all sit beside a literal field.
const addressFieldsFragmentString = `street1
      street2
      street3
      city
      state
      zip
      country
      fullAddress
      name
      company
      phone`

const addressFieldsFragment = gql`
  fragment addressFieldsFragment on Address {
    _id
    ${addressFieldsFragmentString}
  }
`

// The Customer selection both logistic sides read, on the same one-string-two-shapes pattern as
// `addressFieldsFragmentString` above — read that comment for why `_id` is not part of the string.
const vendorOrShipToFragmentString = `profile {
      _id
      name
      picture
      email
    }
    status
    company {
      _id
      name
      domain
    }
    phone`

const vendorOrShipToFragment = gql`
  fragment vendorOrShipToFragment on Customer {
    _id
    ${vendorOrShipToFragmentString}
  }
`

const vendorFragmentString = ` vendor {
      _id
      ${vendorOrShipToFragmentString}
    }
   `

const shipToFragmentString = `shipTo {
      _id
      ${vendorOrShipToFragmentString}
    }
    `

const productFragmentString = `
 product {
      _id
      code
      name
      coverImage
      coverImageThumb
      status
      productType
      requiredSerialNumber
      serialNumberType
      baseUom {
        _id
        symbol
      }
      unitOfMeasurePrecision
    }
`

export const relatedWorkOrdersFragmentString = `
      relatedWorkOrders {
        _id
        code
        status
        product {
          _id
          code
        }
      }`

export const relatedLogisticsFragmentString = `
      relatedLogistics {
        _id
        code
        status
        ${vendorFragmentString}
        ${shipToFragmentString}
        shipDate
        expectedFinishDate
        relatedLogistics {
          _id
          code
        }
      }`

export const logisticsRemarksActivityFragment = gql`
  fragment logisticsRemarksActivityFragment on Activity {
    _id
    activity
    by {
      _id
      profile {
        _id
        name
        picture
      }
    }
    updatedAt
    isDeleted
  }
`
export const remarksActivityFragmentString = `remarksActivity {
  _id
  activity
  by {
    _id
    profile {
      _id
      name
      picture
    }
  }
  updatedAt
  isDeleted
}`

export const logisticFragmentString = `  _id
    code
    shipDate
    shipVia
    status
    summary
    totalQuantity
    remarks
    instructions
    tags
    expectedFinishDate

    ${vendorFragmentString}
    ${shipToFragmentString}
    vendorAddress {
      _id
      ${addressFieldsFragmentString}
    }
    shipToAddress {
      _id
      ${addressFieldsFragmentString}
    }
    items {
      _id
      ${productFragmentString}
      quantity
      remarks
      inventoryCount
      net
      shippingWeight
    }
    gross
    net
    isShippingCostIncluded
    assets {
      _id
      url
      title
      subtitle
      type
      thumbUrl
      mediumUrl
      largeUrl
    }
    ${relatedWorkOrdersFragmentString}
    ${relatedLogisticsFragmentString}
    ${remarksActivityFragmentString}

   `

export const logisticListFragment = gql`
  fragment logisticListFragment on Logistic {
    _id
    code
    shipDate
    shipVia
    status
    summary
    totalQuantity
    remarks
    instructions
    tags
    expectedFinishDate
    vendor {
      ...vendorOrShipToFragment
    }
    shipTo {
      ...vendorOrShipToFragment
    }

    remarksActivity(filter: {}, options: { sort: { updatedAt: -1 }, limit: 10 }) {
      ...logisticsRemarksActivityFragment
    }
  }
  ${logisticsRemarksActivityFragment}
  ${vendorOrShipToFragment}
`
export const logisticListQueries = buildGraphqlQuery('Logistic', logisticListFragment)

export const logisticQueries = buildGraphqlQuery('Logistic', logisticFragmentString, ['items'])

export const logisticCloneQuery = gql`
mutation cloneLogistic($input: CloneLogisticServiceArgs!) {
    cloneLogistic(input: $input) {
        _id
      ${logisticFragmentString}
    }
  }
`

export const buildQuery = (field) => buildGraphqlQuery('Logistic', `_id  ${field}`)

// A `LogisticInlineInput` whose `name` is a nested path (e.g. `shipping.rate.serviceType`) MUST
// pass this as `queryName` — `buildQuery` otherwise defaults to the dotted `name` itself as the
// selection string, which is not valid GraphQL syntax and throws when the mutation config's query
// is parsed. The shipping carrier/packages rows never actually invoke this mutation (their custom
// editContent persists through the dedicated shipping mutations instead), but `useMutation`'s
// `gql()` parse still runs eagerly on mount, so the string must be syntactically valid regardless.
export const logisticShippingFieldsString = `shipping {
    _id
    status
    trackingNumber
    trackingUrl
    pdfLabel
    pngLabel
    zplLabel
    hrefLabel
    isVoidLabel
    voidMessage
    rate {
      _id
      shippingAmount
      deliveryDays
      estimatedDeliveryDate
      serviceType
      serviceCode
      carrierName
      carrierId
    }
    packages {
      _id
      weight {
        unit
        value
      }
      dimensions {
        unit
        length
        width
        height
      }
      insured_value {
        currency
        amount
      }
      label_download {
        png
        pdf
      }
      tracking_number
      package_code
      trackingUrl
      packageType
    }
  }
  isManualShippingRate
  manualShippingCarrier
  shippingOrigin
  totalWeight`

export const logisticUpdateOrderAddressQuery = gql`
  mutation updateLogisticAddress($input: JSON!) {
    updateLogisticAddress(input: $input) {
      _id
      ${logisticFragmentString}
    }
  }
`

export const logisticUpdateProductInventoryQuery = gql`mutation logisticUpdateProductInventory($input: JSON!){
  logisticUpdateProductInventory(input: $input){
      _id
    ${logisticFragmentString}
  }
}`

// This is the fragment the logistic ITEM page actually loads — `singleLogisticQuery` below and the
// updated-logistic subscription (useLogisticUpdatedSubscription.ts) both spread it — so a field
// missing from the address/customer spreads below never reaches the Summary tab's blocks.
export const logisticFragment = gql`
  fragment logisticFragment on Logistic {
    _id
    code
    shipDate
    shipVia
    status
    summary
    totalQuantity
    remarks
    instructions
    tags
    expectedFinishDate
    vendor {
      ...vendorOrShipToFragment
    }
    shipTo {
      ...vendorOrShipToFragment
    }
    vendorAddress {
      ...addressFieldsFragment
    }
    shipToAddress {
      ...addressFieldsFragment
    }
    items {
      _id
      product {
        _id
        code
        name
        coverImage
        coverImageThumb
        status
        productType
        requiredSerialNumber
        serialNumberType
        baseUom {
          _id
          symbol
        }
        unitOfMeasurePrecision
      }
      quantity
      remarks
      inventoryCount
      net
      shippingWeight
    }
    gross
    net
    isShippingCostIncluded
    assets {
      _id
      url
      title
      subtitle
      type
      thumbUrl
      mediumUrl
      largeUrl
    }
    relatedWorkOrders {
      _id
      code
      status
      product {
        _id
        code
      }
    }
    relatedLogistics {
      _id
      code
      status
      vendor {
        ...vendorOrShipToFragment
      }
      shipTo {
        ...vendorOrShipToFragment
      }
      shipDate
      expectedFinishDate
      relatedLogistics {
        _id
        code
      }
    }
    remarksActivity {
      _id
      activity
      by {
        _id
        profile {
          _id
          name
          picture
        }
      }
      updatedAt
      isDeleted
    }
    shippingOrigin
    isManualShippingRate
    manualShippingCarrier
    totalWeight
    activeConversationId
    shipping {
      ...logisticShippingFragment
    }
    communication {
      _id
      email {
        _id
        to
        from
        timeStamp
        content
      }
    }
  }
  ${logisticShippingFragment}
  ${vendorOrShipToFragment}
  ${addressFieldsFragment}
`

export const singleLogisticQuery = gql`
  ${logisticFragment}
  query singleLogistic($filter: JSON) {
    singleLogistic(filter: $filter) {
      ...logisticFragment
    }
  }
`
